import { useEffect, useSyncExternalStore } from 'react';
import { RouterProvider } from 'react-router';
import { CssBaseline } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { Helmet } from '@dr.pogodin/react-helmet';
import router from '@/routes/router';
import ThemeProvider from '@/contexts/ThemeContext';
import { ToastProvider } from '@/contexts/ToastContext';
import QueryClientProviderWrapper from '@/lib/QueryClientProviderWrapper';
import UsageTimeLimitLockoutPage from '@/content/pages/UsageTimeLimitLockout';
import {
  getUsageTimeLimitLockout,
  subscribeUsageTimeLimitLockout,
} from '@/lib/auth/usageTimeLimitLockout';

import '@fontsource/inter/latin-ext-400.css';
import '@fontsource/inter/latin-ext-500.css';
import '@fontsource/inter/latin-ext-600.css';
import '@fontsource/inter/latin-ext-700.css';
import '@fontsource/inter/latin-ext-800.css';

const AppContent = () => {
  const isUsageTimeLimitLockedOut = useSyncExternalStore(
    subscribeUsageTimeLimitLockout,
    getUsageTimeLimitLockout,
    getUsageTimeLimitLockout,
  );

  if (isUsageTimeLimitLockedOut) {
    return <UsageTimeLimitLockoutPage />;
  }

  return <RouterProvider router={router} />;
};

const App = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';

  useEffect(() => {
    // reacts to bfcache all together ( hard reload on all pages)
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  return (
    <>
      <Helmet htmlAttributes={{ lang }} />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ToastProvider>
          <ThemeProvider>
            <QueryClientProviderWrapper>
              {/* {ENV_VARIABLES.ENV_DEV && <ReactQueryDevtools
          initialIsOpen={false} buttonPosition="bottom-left" />} */}
              <AppContent />
            </QueryClientProviderWrapper>
            <CssBaseline />
          </ThemeProvider>
        </ToastProvider>
      </LocalizationProvider>
    </>
  );
};

export default App;
